import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Table, X, Calendar, PanelLeftClose, PanelLeftOpen, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast, FilterX, ListFilter } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ActionMenu from '../../components/common/ActionMenu';
import DownloadModal from '../../components/common/DownloadModal';
import { auditApi } from '../../services/api';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';

import SearchClearIcon from '../../components/common/SearchClearIcon';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import { canonicalQuery, hasColumnFilters, serializeFiltersForServer, SEARCH_HINT } from '../../utils/listSearch';
import StuiSelect from '../../components/common/StuiSelect';
import StuiDateTime from '../../components/common/StuiDateTime';
import { formatTimestamp, localDaysToUtcRange, formatTimestampDate } from '../../utils/dateTime';
const DEFAULT_PAGE_SIZE = 25;

// Filters, sort and search all run on the server (AuditController) — the table is paged and too
// large for the browser. Date is a date column (Year > Month > Day tree of the days present, and
// Date Filters); ID a number column; Old Value and New Value take text conditions only (long text).
const COLUMNS = [
  { key: 'changedAt',        label: 'Date',        width: 188, filterType: 'date' },
  { key: 'changedBy',        label: 'User',        width: 200 },
  { key: 'tableName',        label: 'Table',       width: 120 },
  { key: 'recordPk',         label: 'ID',          width: 60,  filterType: 'number' },
  { key: 'recordIdentifier', label: 'Identifier',  width: 140 },
  { key: 'action',           label: 'Action',      width: 80  },
  { key: 'fieldName',        label: 'Field',       width: 120 },
  { key: 'oldValue',         label: 'Old Value',   width: 360, filterable: 'text' },
  { key: 'newValue',         label: 'New Value',   width: 360, filterable: 'text' },
];

// What search reads (server keys), labelled as the columns read — feeds field: prefixes and the
// Advanced Search builder. The request carries the canonical, key-based form (canonicalQuery).
const SEARCH_FIELD_LABELS = [
  { key: 'changedBy', label: 'User' }, { key: 'tableName', label: 'Table' },
  { key: 'recordIdentifier', label: 'Identifier' }, { key: 'action', label: 'Action' },
  { key: 'fieldName', label: 'Field' }, { key: 'oldValue', label: 'Old Value' },
  { key: 'newValue', label: 'New Value' }, { key: 'module', label: 'Module' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);
const NO_SORT = { key: null, direction: null };

const EXPORT_LIMIT = 5000;

/**
 * Colours live in STUI.css as .stui-badge-audit-* — shared with AuditTrailAdminPage
 * so the two audit pages cannot drift. Previously duplicated inline in both pages,
 * and inconsistent even within this one (10px in the list, 11px in the detail).
 */
const auditBadge = (value) =>
  `stui-badge-audit stui-badge-audit-${String(value || '').toLowerCase()}`;

const AuditTrailCrudPage = () => {
  const { isSysAdmin }  = useAuth();
  const { notify }      = useNotifications();

  const [viewMode, setViewMode]           = useState('list');
  const [rows, setRows]                   = useState([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [pageSize, setPageSize]           = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters]             = useState({});
  const [searchTerm, setSearchTerm]       = useState('');
  const [sortConfig, setSortConfig]       = useState(null); // null = newest first (server default)
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [exportRows, setExportRows]       = useState([]);
  const [selectedRow, setSelectedRow]     = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);

  // Date filter popover
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [appliedFrom, setAppliedFrom]       = useState('');
  const [appliedTo, setAppliedTo]           = useState('');
  const dateFilterRef                       = useRef(null);

  // Purge modal
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeDays, setPurgeDays]           = useState('');
  const [purging, setPurging]               = useState(false);
  const purgeDaysRef                        = useRef(null);
  // Dry-run state. `previewedDays` records WHICH day count the preview belongs to, so
  // changing the number invalidates it and Purge re-locks — you cannot see a count for
  // 365 days, type 30, and delete on the strength of the old number.
  const [purgePreview, setPurgePreview]     = useState(null);
  const [previewedDays, setPreviewedDays]   = useState(null);
  const [previewing, setPreviewing]         = useState(false);

  // Download modal
  const [showDownload, setShowDownload] = useState(false);

  // Enter-triggered search (matches Dynamic Module pattern). The applied value
  // is what gets sent to the API; typing into the box does not fire requests.
  // Backend also enforces a 3-char minimum, so short terms are silently ignored
  // there — clearing the box below-3-char here as a client-side courtesy.
  const [appliedSearch, setAppliedSearch] = useState('');
  const submitSearch = useCallback((value) => {
    const trimmed = (value ?? '').trim();
    setAppliedSearch(trimmed.length >= 3 ? trimmed : '');
  }, []);

  // Everything the server filters by. The list, the datasheet, the count, the dropdown options and
  // the export all use it, so they cannot disagree.
  const queryParams = useCallback(() => {
    const params = {};
    const range = localDaysToUtcRange(appliedFrom, appliedTo);
    if (range.from) params.dateFrom = range.from;
    if (range.to)   params.dateTo   = range.to;
    if (appliedSearch) params.search = canonicalQuery(appliedSearch, SEARCH_FIELDS, SEARCH_FIELD_LABELS);
    // Relative dates (Today, This month, ...) go to the server as explicit days, from this calendar.
    const f = serializeFiltersForServer(filters);
    if (f) params.filters = f;
    return params;
  }, [appliedFrom, appliedTo, appliedSearch, filters]);

  const sortParams = useCallback(() => (
    sortConfig?.key ? { sortKey: sortConfig.key, sortDir: sortConfig.direction } : {}
  ), [sortConfig]);

  const loadData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, pageSize, ...queryParams(), ...sortParams() };
      const res = await auditApi.getAllChanges(params);
      setRows(res.data.data);
      setTotalCount(res.data.totalCount);
      setPage(pg);
      setSelectedRow(null);
    } catch {
      notify.error('Failed to load change history.');
    } finally {
      setLoading(false);
    }
  }, [queryParams, sortParams, pageSize, notify]);

  useEffect(() => { loadData(1); }, [loadData]);

  useEffect(() => {
    const handler = e => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target))
        setShowDateFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showPurgeModal) setTimeout(() => purgeDaysRef.current?.focus(), 50);
  }, [showPurgeModal]);

  const applyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setShowDateFilter(false);
  };

  // The nav item for this page clicked while on it is a new history entry: start clean (search,
  // column filters, sort, date range). The page keeps no memory, so Back does not restore them.
  const location = useLocation();
  const entryKeyRef = useRef(location.key);
  useEffect(() => {
    if (entryKeyRef.current === location.key) return;
    entryKeyRef.current = location.key;
    setSearchTerm(''); setAppliedSearch('');
    setFilters({}); setSortConfig(null);
    setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  }, [location.key]);

  const clearDateFilter = () => {
    setDateFrom(''); setDateTo('');
    setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  };

  const runPurgePreview = async () => {
    const days = parseInt(purgeDays, 10);
    if (!days || days < 1) { notify.error('Enter a valid number of days.'); return; }
    setPreviewing(true);
    try {
      const res = await auditApi.purgePreview(days);
      setPurgePreview(res.data);
      setPreviewedDays(days);
    } catch (err) {
      setPurgePreview(null);
      setPreviewedDays(null);
      notify.error(err?.response?.data?.error || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const closePurgeModal = () => {
    setShowPurgeModal(false);
    setPurgeDays('');
    setPurgePreview(null);
    setPreviewedDays(null);
  };

  const handlePurge = async () => {
    const days = parseInt(purgeDays, 10);
    if (!days || days < 1) { notify.error('Enter a valid number of days.'); return; }
    // Gated on a preview for THIS day count — the whole point of the redesign is that
    // nobody deletes audit history without having seen the number first.
    if (previewedDays !== days) { notify.error('Preview the purge before running it.'); return; }

    const p = purgePreview || {};
    const detail = p.humanRows
      ? `${p.totalRows} record(s), including ${p.humanRows} made by people rather than the Bulk Import Utility.`
      : `${p.totalRows} record(s).`;

    notify.confirm(
      `Permanently delete ${detail} There is no undo.`,
      async () => {
        setPurging(true);
        try {
          const res = await auditApi.purge(days);
          notify.success(`Purged ${res.data.deletedCount} record(s).`);
          closePurgeModal();
          loadData(1);
        } catch (err) {
          notify.error(err?.response?.data?.error || 'Purge failed.');
        } finally {
          setPurging(false);
        }
      }
    );
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const dateFilterActive = !!(appliedFrom || appliedTo);

  const toGridRow = row => ({
    ...row,
    changedAt:        formatTimestamp(row.changedAt) || '—',
    recordIdentifier: row.recordIdentifier || '—',
    fieldName:        row.fieldName  || '—',
    oldValue:         row.oldValue   || '—',
    newValue:         row.newValue   || '—',
  });

  // The server already applied search, filters, date range and sort; this is the current page.
  const visibleData = rows.map(toGridRow);

  // Filter dropdown values come from the whole filtered set, not this page.
  const optionsProvider = colKey =>
    auditApi.getChangeDistinct({ ...queryParams(), column: colKey }).then(r => r.data);

  // Export: every matching row in the current sort (capped), not just the page on screen.
  const openDownload = async () => {
    try {
      const res = await auditApi.exportChanges({ ...queryParams(), ...sortParams(), limit: EXPORT_LIMIT });
      setExportRows((res.data?.rows || []).map(toGridRow));
      if (res.data?.truncated) {
        notify.warning(`Export holds the first ${res.data.returned.toLocaleString()} of ${res.data.totalCount.toLocaleString()} matching rows — narrow the filters for the rest.`);
      }
      setShowDownload(true);
    } catch {
      notify.error('Failed to prepare the export.');
    }
  };

  return (
    <div className="stui-module-container">

      {/* Header */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <div className="stui-module-header-tabs">
            <button
              className={`stui-tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            ><List size={18} /></button>
            <button
              className={`stui-tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`}
              onClick={() => setViewMode('datasheet')}
              title="Datasheet View"
            ><Table size={18} /></button>
          </div>
          <h1>Audit Trail - CRUD</h1>
          <span className="stui-module-header-meta">
            {loading ? '' : `${totalCount.toLocaleString()} record${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="stui-module-header-actions">

          {/* Date filter button + popover */}
          <div className="stui-popover-anchor" ref={dateFilterRef}>
            <button
              className={`stui-crud-btn${dateFilterActive ? ' active' : ''}`}
              onClick={() => setShowDateFilter(v => !v)}
              title={dateFilterActive ? `Filtered: ${appliedFrom || '…'} – ${appliedTo || '…'}` : 'Filter by date'}
            ><Calendar size={17} /></button>
            {showDateFilter && (
              <div className="stui-popover">
                <div className="stui-field-group">
                  <label>From</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateFrom} onChange={setDateFrom} />
                </div>
                <div className="stui-field-group">
                  <label>To</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateTo} onChange={setDateTo} />
                </div>
                <div className="stui-popover-footer">
                  {(dateFrom || dateTo || appliedFrom || appliedTo) && (
                    <button className="stui-btn stui-btn-secondary" onClick={clearDateFilter}>Clear</button>
                  )}
                  <button className="stui-btn stui-btn-primary" onClick={applyDateFilter}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* Clear column filters — standard chip, shows the filtered count */}
          {hasColumnFilters(filters) && (
            <button onClick={() => setFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{totalCount.toLocaleString()}</span>
            </button>
          )}

          {/* Search */}
          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => { setSearchTerm(''); submitSearch(''); }} />
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search changes (3+ chars, press Enter)..."
              title={SEARCH_HINT}
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                if (!e.target.value) submitSearch('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitSearch(e.target.value);
                }
              }}
            />
          </div>

          {/* 6-dot */}
          <ActionMenu
            items={[{ key: 'adv-search', icon: <ListFilter size={16} />, label: 'Advanced Search…', onClick: () => setShowAdvSearch(true) }]}
            onDownload={openDownload}
            downloadLabel="Export…"
            showPurge={isSysAdmin}
            onPurge={() => setShowPurgeModal(true)}
          />
        </div>
      </div>

      {/* Content */}
      {viewMode === 'datasheet' ? (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={visibleData}
            columns={COLUMNS}
            filters={filters}
            setFilters={setFilters}
            optionsProvider={optionsProvider}
            sortConfig={sortConfig || NO_SORT}
            onSortChange={setSortConfig}
          />
          <Pagination page={page} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} rowCount={visibleData.length} loading={loading} onPage={loadData} onPageSize={ps => { setPageSize(ps); }} />
        </div>
      ) : (
        <div className="stui-list-view">
          {/* Left: list panel */}
          <div className={`stui-list-panel${listCollapsed ? ' collapsed' : ''}`}>
            <div className="stui-list-panel-header">
              {listCollapsed ? (
                <button className="stui-list-panel-expand-btn" onClick={() => setListCollapsed(false)} title="Expand panel">
                  <PanelLeftOpen size={16} />
                </button>
              ) : (
                <button className="stui-list-panel-collapse-btn" onClick={() => setListCollapsed(true)} title="Collapse panel">
                  <PanelLeftClose size={16} />
                </button>
              )}
            </div>
            <div className="stui-list-scroll" style={listCollapsed ? { display: 'none' } : {}}>
              {loading && (
                <div className="stui-list-empty">
                  Loading...
                </div>
              )}
              {!loading && visibleData.length === 0 && (
                <div className="stui-list-empty">
                  No records found.
                </div>
              )}
              {visibleData.map((row, i) => {
                const isSelected = selectedRow === i;
                return (
                  <button
                    key={i}
                    className={`stui-list-item${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedRow(isSelected ? null : i)}
                  >
                    <div className="stui-row-between">
                      <span className="stui-list-item-primary">
                        {row.recordIdentifier !== '—' ? row.recordIdentifier : `Record #${row.recordPk}`}
                      </span>
                      <span className={auditBadge(row.action)}>{row.action}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: detail panel */}
          <div className="stui-detail-panel">
            {selectedRow == null ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                Select a record to view details
              </div>
            ) : (() => {
              const row = visibleData[selectedRow];
              return (
                <div className="stui-detail-content">
                  <div className="stui-detail-grid">
                    <div className="stui-field-group label-left stui-col-12">
                      <label>Date</label>
                      <span className="stui-field-display">{row.changedAt}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-12">
                      <label>User</label>
                      <span className="stui-field-display">{row.changedBy}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Table</label>
                      <span className="stui-field-display">{row.tableName}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Record ID</label>
                      <span className="stui-field-display">{row.recordPk}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Identifier</label>
                      <span className="stui-field-display">{row.recordIdentifier}</span>
                    </div>
                    <div className="stui-field-group label-left stui-col-8">
                      <label>Action</label>
                      <span className="stui-field-display">
                        <span className={auditBadge(row.action)}>{row.action}</span>
                      </span>
                    </div>
                    {row.fieldName !== '—' && (
                      <div className="stui-field-group label-left stui-col-16">
                        <label>Field</label>
                        <span className="stui-field-display">{row.fieldName}</span>
                      </div>
                    )}
                    <div className="stui-field-group label-left stui-col-24">
                      <label>Old Value</label>
                      <span className="stui-field-display stui-break-word">
                        {row.action === 'CREATE' ? 'N/A' : row.oldValue}
                      </span>
                    </div>
                    <div className="stui-field-group label-left stui-col-24">
                      <label>New Value</label>
                      <span className="stui-field-display stui-break-word">
                        {row.action === 'DELETE' ? 'N/A' : row.newValue}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Pagination — list view only (datasheet has it inline above) */}
      {viewMode === 'list' && (
        <Pagination page={page} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} rowCount={visibleData.length} loading={loading} onPage={loadData} onPageSize={ps => setPageSize(ps)} />
      )}

      {/* Purge modal */}
      {showPurgeModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3 className="stui-modal-title">Purge Old Records</h3>
              <button className="stui-modal-close" onClick={closePurgeModal}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-hint">
                Permanently deletes audit records older than the given number of days for the
                current database. There is no undo and no recycle bin.
              </p>

              <div className="stui-form-row-inline">
                <label className="stui-label">Delete records older than</label>
                <input
                  ref={purgeDaysRef}
                  type="number"
                  min="1"
                  className="stui-field-input stui-inner-table-input-sm"
                  value={purgeDays}
                  onChange={e => {
                    setPurgeDays(e.target.value);
                    // Any edit invalidates the previous count.
                    setPurgePreview(null);
                    setPreviewedDays(null);
                  }}
                  onKeyDown={e => e.key === 'Enter' && runPurgePreview()}
                  placeholder="days"
                />
                <span className="stui-label">days</span>
                <button
                  className="stui-btn stui-btn-secondary stui-btn-sm"
                  onClick={runPurgePreview}
                  disabled={previewing || !purgeDays}
                >
                  {previewing ? 'Checking...' : 'Preview'}
                </button>
              </div>

              {purgePreview && previewedDays === parseInt(purgeDays, 10) && (
                <>
                  <div className="stui-detail-section-header">Would delete</div>
                  <table className="stui-inner-table">
                    <tbody>
                      <tr>
                        <td>Total records</td>
                        <td className="stui-cell-right stui-fw-semibold">{purgePreview.totalRows}</td>
                      </tr>
                      <tr>
                        <td>Bulk Import Utility</td>
                        <td className="stui-cell-right">{purgePreview.bulkImportRows}</td>
                      </tr>
                      <tr>
                        <td>Human edits</td>
                        <td className={`stui-cell-right ${purgePreview.humanRows > 0 ? 'stui-status-failed' : ''}`}>
                          {purgePreview.humanRows}
                        </td>
                      </tr>
                      {purgePreview.oldestAffected && (
                        <tr>
                          <td>Date range affected</td>
                          <td className="stui-cell-right stui-mono">
                            {formatTimestampDate(purgePreview.oldestAffected)} &rarr; {formatTimestampDate(purgePreview.newestAffected)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {purgePreview.totalRows === 0 && (
                    <p className="stui-hint">Nothing is older than {previewedDays} days — there is nothing to purge.</p>
                  )}
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={closePurgeModal}>Cancel</button>
              <button
                className="stui-btn stui-btn-danger"
                onClick={handlePurge}
                disabled={purging || previewedDays !== parseInt(purgeDays, 10) || !purgePreview?.totalRows}
              >
                {purging ? 'Purging...' : 'Purge'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdvancedSearchModal
        isOpen={showAdvSearch}
        onClose={() => setShowAdvSearch(false)}
        query={searchTerm}
        fields={SEARCH_FIELD_LABELS}
        onApply={q => { setSearchTerm(q); submitSearch(q); }}
      />

      <DownloadModal
        isOpen={showDownload}
        onClose={() => setShowDownload(false)}
        data={exportRows}
        columns={COLUMNS}
        tableName="Audtrl_CRUD"
      />
    </div>
  );
};

const Pagination = ({ page, totalPages, totalCount, pageSize, rowCount, loading, onPage, onPageSize }) => (
  <div className="stui-pagination">
    <div className="stui-pagination-info">
      <span>Showing {rowCount.toLocaleString()} of {totalCount.toLocaleString()} records</span>
    </div>
    <div className="stui-pagination-controls">
      <label className="stui-pagination-pagesize">
        <span>Per page:</span>
        <StuiSelect value={pageSize} onChange={e => { onPageSize(Number(e.target.value)); onPage(1); }}>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </StuiSelect>
      </label>
      <div className="stui-pagination-nav">
        <button className="stui-pagination-btn" onClick={() => onPage(1)} disabled={page <= 1 || loading} title="First page"><ChevronFirst size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(page - 1)} disabled={page <= 1 || loading} title="Previous page"><ChevronLeft size={18} /></button>
        <span className="stui-pagination-page-info">Page {page} of {totalPages}</span>
        <button className="stui-pagination-btn" onClick={() => onPage(page + 1)} disabled={page >= totalPages || loading} title="Next page"><ChevronRight size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(totalPages)} disabled={page >= totalPages || loading} title="Last page"><ChevronLast size={18} /></button>
      </div>
    </div>
  </div>
);

export default AuditTrailCrudPage;
